/**
 * React Query hooks for Lead Management
 * 
 * Provides hooks for fetching, creating, updating, and deleting leads
 * with automatic cache management and optimistic updates.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { leadService } from '../services/leadService';
import { 
  LeadCreate, 
  LeadUpdate, 
  LeadFilters, 
  BulkImportRequest,
  SegmentCreate 
} from '../types/lead';

/**
 * Hook for fetching paginated list of leads with filters
 * 
 * @param params - Query parameters including page, per_page, and filter fields
 * @returns Query result with leads data, loading state, and error
 * 
 * @example
 * const { data, isLoading, error, refetch } = useLeads({
 *   page: 1,
 *   per_page: 20,
 *   status: ['new', 'contacted'],
 *   sort_by: 'created_at',
 *   sort_order: 'desc'
 * });
 */
export const useLeads = (params: {
  page: number;
  per_page: number;
  gst_verification_status?: string[];
  location?: string;
  city?: string;
  state?: string;
  source?: string[];
  interest_category?: string;
  status?: string[];
  campaign_tag?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}) => {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: () => leadService.getAll(params),
    staleTime: 30 * 1000, // 30 seconds - refresh more frequently for better UX
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });
};

/**
 * Hook for fetching a single lead by ID
 * 
 * @param id - Lead ID
 * @returns Query result with lead data, loading state, and error
 * 
 * @example
 * const { data: lead, isLoading, error } = useLead(leadId);
 */
export const useLead = (id: string | undefined) => {
  return useQuery({
    queryKey: ['lead', id],
    queryFn: () => leadService.getById(id!),
    enabled: !!id, // Only fetch if ID is provided
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook for creating a new lead
 * 
 * Automatically invalidates the leads list cache on success
 * and shows success/error notifications.
 * 
 * @returns Mutation object with mutate function and loading state
 * 
 * @example
 * const createLead = useCreateLead();
 * 
 * const handleSubmit = async (leadData) => {
 *   await createLead.mutateAsync(leadData);
 *   navigate('/admin/leads');
 * };
 */
export const useCreateLead = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (leadData: LeadCreate) => leadService.create(leadData),
    onSuccess: () => {
      // Invalidate leads list to refetch with new lead
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      
      // Show success notification
      enqueueSnackbar('Lead created successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      // Show error notification
      const errorMessage = error?.response?.data?.detail || 
        error?.message || 
        'Failed to create lead';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

/**
 * Hook for updating an existing lead
 * 
 * Automatically invalidates both the leads list and the specific lead cache
 * on success and shows success/error notifications.
 * 
 * @returns Mutation object with mutate function and loading state
 * 
 * @example
 * const updateLead = useUpdateLead();
 * 
 * const handleUpdate = async () => {
 *   await updateLead.mutateAsync({ id: leadId, data: updatedData });
 * };
 */
export const useUpdateLead = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LeadUpdate }) =>
      leadService.update(id, data),
    onSuccess: (_, variables) => {
      // Invalidate leads list to refetch with updated lead
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      
      // Invalidate specific lead cache
      queryClient.invalidateQueries({ queryKey: ['lead', variables.id] });
      
      // Show success notification
      enqueueSnackbar('Lead updated successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      // Show error notification
      const errorMessage = error?.response?.data?.detail || 
        error?.message || 
        'Failed to update lead';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

/**
 * Hook for deleting a lead (soft delete)
 * 
 * Automatically invalidates the leads list cache on success
 * and shows success/error notifications.
 * 
 * @returns Mutation object with mutate function and loading state
 * 
 * @example
 * const deleteLead = useDeleteLead();
 * 
 * const handleDelete = async () => {
 *   if (confirm('Are you sure you want to delete this lead?')) {
 *     await deleteLead.mutateAsync(leadId);
 *     navigate('/admin/leads');
 *   }
 * };
 */
export const useDeleteLead = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (id: string) => leadService.delete(id),
    onSuccess: () => {
      // Invalidate leads list to refetch without deleted lead
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      
      // Show success notification
      enqueueSnackbar('Lead deleted successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      // Show error notification
      const errorMessage = error?.response?.data?.detail || 
        error?.message || 
        'Failed to delete lead';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

/**
 * Hook for bulk importing leads from CSV/Excel
 * 
 * Automatically invalidates the leads list cache on success
 * and shows detailed success/error notifications with import results.
 * 
 * @returns Mutation object with mutate function and loading state
 * 
 * @example
 * const bulkImport = useBulkImportLeads();
 * 
 * const handleImport = async (fileContent: string, fileType: 'csv' | 'excel') => {
 *   const result = await bulkImport.mutateAsync({ fileContent, fileType });
 *   console.log(`Imported ${result.successful} leads, ${result.failed} failed`);
 * };
 */
export const useBulkImportLeads = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: BulkImportRequest) => leadService.bulkImport(data),
    onSuccess: (result) => {
      // Invalidate leads list to refetch with new leads
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      
      // Show detailed success notification
      const message = `Import complete: ${result.successful} successful, ${result.failed} failed`;
      enqueueSnackbar(message, { 
        variant: result.failed > 0 ? 'warning' : 'success',
        autoHideDuration: 5000 
      });
    },
    onError: (error: any) => {
      // Show error notification
      const errorMessage = error?.response?.data?.detail || 
        error?.message || 
        'Failed to import leads';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

/**
 * Hook for exporting leads to CSV
 * 
 * Downloads the CSV file automatically on success
 * and shows success/error notifications.
 * 
 * @returns Mutation object with mutate function and loading state
 * 
 * @example
 * const exportLeads = useExportLeads();
 * 
 * const handleExport = async () => {
 *   await exportLeads.mutateAsync({ 
 *     status: ['new', 'contacted'],
 *     gst_verification_status: ['verified']
 *   });
 * };
 */
export const useExportLeads = () => {
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (filters?: LeadFilters) => leadService.export(filters),
    onSuccess: (blob) => {
      // Create download link and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Show success notification
      enqueueSnackbar('Leads exported successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      // Show error notification
      const errorMessage = error?.response?.data?.detail || 
        error?.message || 
        'Failed to export leads';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};

/**
 * Hook for fetching saved segments
 * 
 * @returns Query result with segments data, loading state, and error
 * 
 * @example
 * const { data: segments, isLoading } = useLeadSegments();
 */
export const useLeadSegments = () => {
  return useQuery({
    queryKey: ['lead-segments'],
    queryFn: () => leadService.getSegments(),
    staleTime: 10 * 60 * 1000, // 10 minutes - segments don't change frequently
  });
};

/**
 * Hook for creating a new saved segment
 * 
 * Automatically invalidates the segments cache on success
 * and shows success/error notifications.
 * 
 * @returns Mutation object with mutate function and loading state
 * 
 * @example
 * const createSegment = useCreateLeadSegment();
 * 
 * const handleSaveSegment = async () => {
 *   await createSegment.mutateAsync({
 *     name: 'High Value Leads',
 *     description: 'Leads with GST verification',
 *     filter_criteria: { gst_verification_status: ['verified'] }
 *   });
 * };
 */
export const useCreateLeadSegment = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (segmentData: SegmentCreate) => leadService.createSegment(segmentData),
    onSuccess: () => {
      // Invalidate segments list to refetch with new segment
      queryClient.invalidateQueries({ queryKey: ['lead-segments'] });
      
      // Show success notification
      enqueueSnackbar('Segment saved successfully', { variant: 'success' });
    },
    onError: (error: any) => {
      // Show error notification
      const errorMessage = error?.response?.data?.detail || 
        error?.message || 
        'Failed to save segment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
  });
};
